import { useState, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import classNames from 'clsx';
import { useWindowSize } from '../../../hooks/useWindowSize';
import css from './menu.module.scss';

/**
 * Menu component Renders a list of items
 * @param {string} className - The class to be applied
 * @param {array} items - The items to be displayed
 * @param {function} onClose - callback for close event handler
 */
const Menu = ({ className, items, onClose }) => {
  const inputRef = useRef();
  const [width, height] = useWindowSize();

  const [isShowSubItems, showSubItems] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    const {
      top,
      left,
      height: elHeight,
      width: elWidth,
    } = inputRef.current.getBoundingClientRect();

    if (!width || !height || !elHeight || !elWidth) {
      setPositionX(0);
      setPositionY(0);
      return;
    }

    if (elWidth + left > width) {
      setPositionX(elWidth + left - width + 10);
    } else {
      setPositionX(0);
    }
    if (elHeight + top > height) {
      setPositionY(elHeight + top - height + 10);
    } else {
      setPositionY(0);
    }
  }, [width, height]);

  return (
    <ul
      style={{
        ...(positionY && { top: -positionY }),
        ...(positionX && { left: `calc( 100% - ${positionX}px )` }),
      }}
      ref={inputRef}
      className={classNames(className, [css.menu])}
      data-testid="dropDownEditMenu"
    >
      {items.map(
        (
          {
            title,
            onClick,
            items: subItems,
            className: itemClassName,
            ...itemProps
          },
          index,
        ) =>
          title ? (
            <li
              key={index}
              {...itemProps}
              className={classNames(itemClassName, {
                [css.subMenu]: !!subItems,
              })}
              onMouseEnter={() => showSubItems(true)}
              onMouseLeave={() => showSubItems(false)}
            >
              {onClick ? (
                <button
                  onClick={() => {
                    if (onClose) {
                      onClose();
                    }
                    onClick();
                  }}
                >
                  {title}
                </button>
              ) : (
                <span>{title}</span>
              )}

              {subItems && <i className={css.arrowRight} />}
              {subItems && isShowSubItems && (
                <Menu items={subItems} onClose={onClose} />
              )}
            </li>
          ) : (
            <li key={index} className={css.divider} {...itemProps} />
          ),
      )}
    </ul>
  );
};

Menu.propTypes = {
  className: propTypes.string,
  items: propTypes.array,
  onClose: propTypes.func,
};

export default Menu;
